import type {
  BulkQuestionnaireAssignmentResult,
  QuestionnaireAssignment,
  QuestionnaireAssignmentSummary,
  QuestionnaireDeliveryAttempt,
  QuestionnaireDeliveryEvent,
  QuestionnaireSendStatus,
} from "@/types";

export type AssignmentStatusGroup =
  | "action_needed"
  | "awaiting"
  | "submitted"
  | "failed"
  | "expired"
  | "completed"
  | "cancelled";

const AWAITING_STATUSES = new Set<QuestionnaireSendStatus>([
  "scheduled",
  "queued",
  "pending",
  "sent",
  "awaiting",
  "delivered",
  "opened",
  "in_progress",
]);

export function assignmentStatusGroup(
  status: QuestionnaireSendStatus,
  actionRequired = false,
): AssignmentStatusGroup {
  if (actionRequired || status === "action_required" || status === "action_needed") {
    return "action_needed";
  }
  if (AWAITING_STATUSES.has(status)) return "awaiting";
  if (status === "submitted") return "submitted";
  if (status === "reviewed" || status === "completed") return "completed";
  if (status === "failed") return "failed";
  if (status === "expired") return "expired";
  return "cancelled";
}

type SummaryEnvelope =
  | { counts?: Record<string, number> }
  | Partial<QuestionnaireAssignmentSummary>;

function countStatuses(counts: Record<string, number>, statuses: string[]): number {
  return statuses.reduce((total, status) => total + (counts[status] ?? 0), 0);
}

export function adaptAssignmentSummary(
  payload: SummaryEnvelope,
): QuestionnaireAssignmentSummary {
  const counts =
    "counts" in payload && payload.counts
      ? payload.counts
      : (payload as Record<string, number>);

  const actionNeeded = countStatuses(counts, ["action_required", "action_needed"]);
  const awaiting = countStatuses(counts, [
    "scheduled",
    "queued",
    "pending",
    "sent",
    "awaiting",
    "delivered",
    "opened",
    "in_progress",
  ]);
  const submitted = counts.submitted ?? 0;
  const completed = countStatuses(counts, ["reviewed", "completed"]);
  const failed = counts.failed ?? 0;
  const expired = counts.expired ?? 0;
  const denominator =
    counts.total ??
    actionNeeded + awaiting + submitted + completed + failed + expired + (counts.cancelled ?? 0);

  return {
    action_needed: actionNeeded,
    awaiting,
    submitted,
    failed,
    expired,
    completed,
    completion_rate:
      typeof (payload as Partial<QuestionnaireAssignmentSummary>).completion_rate === "number"
        ? (payload as Partial<QuestionnaireAssignmentSummary>).completion_rate
        : denominator > 0
          ? ((submitted + completed + actionNeeded) / denominator) * 100
          : 0,
    median_completion_minutes:
      (payload as Partial<QuestionnaireAssignmentSummary>).median_completion_minutes,
    overdue_rate: (payload as Partial<QuestionnaireAssignmentSummary>).overdue_rate,
    channel_delivery: (payload as Partial<QuestionnaireAssignmentSummary>).channel_delivery,
    template_usage: (payload as Partial<QuestionnaireAssignmentSummary>).template_usage,
    automation_performance:
      (payload as Partial<QuestionnaireAssignmentSummary>).automation_performance,
  };
}

function deliveryOccurredAt(delivery: QuestionnaireDeliveryAttempt): string {
  return (
    delivery.delivered_at ??
    delivery.failed_at ??
    delivery.sent_at ??
    delivery.scheduled_at ??
    delivery.updated_at ??
    delivery.created_at
  );
}

export function deliveryEventsFromAttempts(
  deliveries: QuestionnaireDeliveryAttempt[] | undefined,
): QuestionnaireDeliveryEvent[] {
  return (deliveries ?? []).map((delivery) => ({
    id: delivery.id,
    event: delivery.status,
    occurred_at: deliveryOccurredAt(delivery),
    channel: delivery.channel,
    detail:
      delivery.failure_code
        ? `Delivery ${delivery.status} (${delivery.failure_code})`
        : `${delivery.channel} attempt ${delivery.attempt}`,
  }));
}

export function templateRequiresChartReview(chartMapping?: string): boolean {
  if (!chartMapping?.trim()) return false;
  try {
    const mapping = JSON.parse(chartMapping) as {
      requires_review?: boolean;
      auto_write?: boolean;
      targets?: unknown[];
    };
    return (
      mapping.requires_review === true &&
      mapping.auto_write !== true &&
      Array.isArray(mapping.targets) &&
      mapping.targets.length > 0
    );
  } catch {
    return false;
  }
}

export function adaptAssignment(
  assignment: QuestionnaireAssignment,
): QuestionnaireAssignment {
  const version =
    assignment.questionnaire_version ?? assignment.template_version;
  const events = assignment.delivery_events ??
    (assignment.deliveries ? deliveryEventsFromAttempts(assignment.deliveries) : undefined);
  return {
    ...assignment,
    ...(version ? { questionnaire_version: version } : {}),
    ...(events ? { delivery_events: events } : {}),
  };
}

type BulkEnvelope =
  | BulkQuestionnaireAssignmentResult
  | QuestionnaireAssignment[]
  | { assignments?: QuestionnaireAssignment[]; errors?: BulkQuestionnaireAssignmentResult["errors"] };

export function adaptBulkAssignmentResult(
  payload: BulkEnvelope,
): BulkQuestionnaireAssignmentResult {
  if (Array.isArray(payload)) {
    return { created: payload.map(adaptAssignment), errors: [] };
  }
  const created =
    "created" in payload
      ? payload.created
      : "assignments" in payload
        ? (payload.assignments ?? [])
        : [];
  return {
    created: created.map(adaptAssignment),
    errors: payload.errors ?? [],
  };
}
